import React, { useState, useCallback } from "react"
import PropTypes from "prop-types"
import errorsPropType from "components/PropTypes/errorsPropType"

import { isEqual } from "underscore"

import TextField from "components/MemoisedTextField"

import CountrySelect from "components/CountrySelect"
import FormFieldset from "components/FormFieldset"

import Analytics from "lib/Analytics"
import i18n from "lib/i18n"

const AddressDetails = ({
  active,
  objectName,
  showOptionalAddress,
  address,
  city,
  state,
  postcode,
  homeCountryCode,
  country,
  onChange,
  errors,
}) => {
  const [secondAddress, setSecondAddress] = useState("")

  // Analytics tracking
  const track = (field) => (event) => {
    const value = event.target.value
    if (value.length) {
      Analytics.trackFormEvent("Donation Form", "Completed", field)
    } else {
      Analytics.trackFormEvent("Donation Form", "Skipped", field)
    }
  }

  const trackSecondaryAddress = useCallback(track("Secondary Address"), [Analytics])
  const trackAddress = useCallback(track("Address"), [Analytics])
  const trackCity = useCallback(track("City"), [Analytics])
  const trackState = useCallback(track("State"), [Analytics])
  const trackCountry = useCallback(track("Country"), [Analytics])
  const trackPostcode = useCallback(track("Postcode"), [Analytics])

  // Change handling
  const handleChange = (field) => (event) => onChange({ [field]: event.target.value })

  const setAddress = useCallback(handleChange("address"), [onChange])
  const setCity = useCallback(handleChange("city"), [onChange])
  const setCountryDetails = useCallback(onChange, [onChange])
  const setPostcode = useCallback(handleChange("postcode"), [onChange])
  const setState = useCallback(handleChange("state"), [onChange])

  const onSecondAddressChange = useCallback(
    (event) => {
      setSecondAddress(event.target.value)
      onChange({ secondAddress: event.target.value })
    },
    [setSecondAddress, onChange]
  )

  const optionalAddressField = () => {
    if (!showOptionalAddress) {
      return ""
    }

    return (
      <FormFieldset>
        <TextField
          autoComplete
          disabled={!active}
          fullWidth
          id={`${objectName}_second_address`}
          label={i18n.t("donation.placeholder.second_address")}
          name={`${objectName}[second_address]`}
          onBlur={trackSecondaryAddress}
          onChange={onSecondAddressChange}
          type="text"
          value={secondAddress}
        />
      </FormFieldset>
    )
  }

  return (
    <div>
      <FormFieldset>
        <TextField
          autoComplete
          disabled={!active}
          error={!!errors.address}
          fullWidth
          label={i18n.t("donation.placeholder.your_home_address")}
          helperText={errors.address}
          id={`${objectName}_address`}
          name={`${objectName}[address]`}
          onBlur={trackAddress}
          onChange={setAddress}
          type="text"
          value={address}
        />
      </FormFieldset>

      {optionalAddressField()}

      <FormFieldset>
        <TextField
          autoComplete
          disabled={!active}
          error={!!errors.city}
          fullWidth
          helperText={errors.city}
          id={`${objectName}_city`}
          label={i18n.t("donation.placeholder.your_city")}
          name={`${objectName}[city]`}
          onBlur={trackCity}
          onChange={setCity}
          type="text"
          value={city}
        />
      </FormFieldset>

      <FormFieldset>
        <TextField
          autoComplete
          disabled={!active}
          error={!!errors.state}
          fullWidth
          label={i18n.t("donation.placeholder.your_state")}
          helperText={errors.state}
          id={`${objectName}_state`}
          name={`${objectName}[state]`}
          onBlur={trackState}
          onChange={setState}
          type="text"
          value={state}
        />
      </FormFieldset>

      <FormFieldset>
        <CountrySelect
          active={active}
          defaultCountryCode={homeCountryCode}
          error={!!errors.country}
          helperText={errors.country}
          id={`${objectName}_country`}
          name={`${objectName}[country]`}
          onBlur={trackCountry}
          onChange={setCountryDetails}
          value={country}
        />
      </FormFieldset>

      <FormFieldset>
        <TextField
          autoComplete
          disabled={!active}
          error={!!errors.postcode}
          fullWidth
          label={i18n.t("donation.placeholder.your_postcode")}
          helperText={errors.postcode}
          id={`${objectName}_postcode`}
          name={`${objectName}[postcode]`}
          onBlur={trackPostcode}
          onChange={setPostcode}
          type="text"
          value={postcode}
        />
      </FormFieldset>
    </div>
  )
}

AddressDetails.defaultProps = {
  active: true,
  address: "",
  city: "",
  country: "",
  errors: {},
  homeCountryCode: "AU",
  objectName: "donation",
  onChange: () => {},
  postcode: "",
  showOptionalAddress: false,
  state: "",
}

AddressDetails.propTypes = {
  active: PropTypes.bool,
  address: PropTypes.string,
  city: PropTypes.string,
  country: PropTypes.string,
  errors: errorsPropType,
  homeCountryCode: PropTypes.string,
  objectName: PropTypes.string,
  onChange: PropTypes.func,
  postcode: PropTypes.string,
  showOptionalAddress: PropTypes.bool,
  state: PropTypes.string,
}

export default React.memo(AddressDetails, (prevProps, nextProps) => isEqual(prevProps, nextProps))
